import React from 'react';
import {
  Modal,
  Stack,
  Text,
  Group,
  Badge,
  Paper,
  SimpleGrid,
  Progress,
  Table,
  ScrollArea,
  Button,
  Divider,
  Box
} from '@mantine/core';
import {
  IconTrendingUp,
  IconTrendingDown,
  IconMinus,
  IconUsers,
  IconClipboardList,
  IconChartBar
} from '@tabler/icons-react';

interface ComparisonData {
  beforeAssessmentID: string;
  afterAssessmentID: string;
  beforeAssessmentName: string;
  afterAssessmentName: string;
  improvementRate: number;
  participantComparison: Array<{
    participantID: string;
    beforeScore: number;
    afterScore: number;
    improvement: number;
  }>;
  moduleComparison: Array<{
    moduleID: string;
    moduleName: string;
    beforeAverage: number;
    afterAverage: number;
    improvement: number;
  }>;
  // NEW: SkalaCategory comparison
  skalaComparison?: Array<{
    skalaCategory: string;
    beforeAverage: number;
    afterAverage: number;
    improvement: number;
    beforeResponseCount: number;
    afterResponseCount: number;
  }>;
  beforeSkalaDetails?: Array<{
    skalaCategory: string;
    averageScore: number;
    responseCount: number;
  }>;
  afterSkalaDetails?: Array<{
    skalaCategory: string;
    averageScore: number;
    responseCount: number;
  }>;
}

interface ComparisonDetailModalProps {
  opened: boolean;
  onClose: () => void;
  comparisonData: ComparisonData | null;
  isLoading?: boolean;
}

const ComparisonDetailModal: React.FC<ComparisonDetailModalProps> = ({
  opened,
  onClose,
  comparisonData,
  isLoading = false
}) => {
  if (!comparisonData && !isLoading) return null;

  const getImprovementColor = (improvement: number) => {
    if (improvement > 0) return 'green';
    if (improvement < 0) return 'red';
    return 'gray';
  };

  const getImprovementIcon = (improvement: number) => {
    if (improvement > 0) return <IconTrendingUp size={14} />;
    if (improvement < 0) return <IconTrendingDown size={14} />;
    return <IconMinus size={14} />;
  };

  const ComparisonCard = ({
    title,
    beforeValue,
    afterValue,
    improvement,
    icon,
    subtitle
  }: {
    title: string;
    beforeValue: number;
    afterValue: number;
    improvement: number;
    icon: React.ReactNode;
    subtitle?: string;
  }) => (
    <Paper p='md' withBorder radius='md'>
      <Group justify='space-between' mb='sm'>
        <Group gap='xs'>
          {icon}
          <Text fw={500} size='sm'>
            {title}
          </Text>
        </Group>
        <Badge
          color={getImprovementColor(improvement)}
          variant='light'
          size='sm'
          leftSection={getImprovementIcon(improvement)}
        >
          {improvement > 0 ? '+' : ''}
          {improvement.toFixed(1)}
        </Badge>
      </Group>

      <SimpleGrid cols={2} spacing='xs'>
        <Box>
          <Text size='xs' c='dimmed'>
            Before
          </Text>
          <Text fw={600} size='lg'>
            {beforeValue.toFixed(1)}
          </Text>
        </Box>
        <Box>
          <Text size='xs' c='dimmed'>
            After
          </Text>
          <Text fw={600} size='lg'>
            {afterValue.toFixed(1)}
          </Text>
        </Box>
      </SimpleGrid>

      {subtitle && (
        <Text size='xs' c='dimmed' mt='xs'>
          {subtitle}
        </Text>
      )}

      <Progress
        value={((afterValue - beforeValue) / beforeValue) * 100 + 100}
        color={getImprovementColor(improvement)}
        size='xs'
        mt='sm'
      />
    </Paper>
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size='xl'
      title={
        <Group gap='md'>
          <IconChartBar size={20} />
          <Text fw={600} size='lg'>
            Assessment Comparison Analysis
          </Text>
        </Group>
      }
      centered
    >
      {isLoading ? (
        <Stack gap='md' p='md'>
          <Text>Loading comparison data...</Text>
        </Stack>
      ) : (
        comparisonData && (
          <Stack gap='lg'>
            {/* Header Section */}
            <Paper p='md' bg='blue.0' radius='md'>
              <SimpleGrid cols={2} spacing='md'>
                <Box>
                  <Text size='xs' c='dimmed' fw={500}>
                    BEFORE ASSESSMENT
                  </Text>
                  <Text fw={600} size='md'>
                    {comparisonData.beforeAssessmentName}
                  </Text>
                </Box>
                <Box>
                  <Text size='xs' c='dimmed' fw={500}>
                    AFTER ASSESSMENT
                  </Text>
                  <Text fw={600} size='md'>
                    {comparisonData.afterAssessmentName}
                  </Text>
                </Box>
              </SimpleGrid>

              <Divider my='md' />

              <Group justify='center'>
                <Badge
                  size='lg'
                  color={getImprovementColor(comparisonData.improvementRate)}
                  variant='filled'
                  leftSection={getImprovementIcon(comparisonData.improvementRate)}
                >
                  Overall Improvement: {comparisonData.improvementRate > 0 ? '+' : ''}
                  {comparisonData.improvementRate.toFixed(1)}%
                </Badge>
              </Group>
            </Paper>

            {/* SkalaCategory Comparison */}
            {comparisonData.skalaComparison && comparisonData.skalaComparison.length > 0 && (
              <Stack gap='md'>
                <Text fw={600} size='md'>
                  SkalaCategory Analysis
                </Text>

                <SimpleGrid cols={{ base: 1, md: 2 }} spacing='md'>
                  {comparisonData.skalaComparison.map((skala) => (
                    <ComparisonCard
                      key={skala.skalaCategory}
                      title={skala.skalaCategory}
                      beforeValue={skala.beforeAverage}
                      afterValue={skala.afterAverage}
                      improvement={skala.improvement}
                      icon={<IconClipboardList size={16} />}
                      subtitle={`Responses: ${skala.beforeResponseCount} → ${skala.afterResponseCount}`}
                    />
                  ))}
                </SimpleGrid>
              </Stack>
            )}

            {/* Side-by-Side SkalaCategory Details */}
            {comparisonData.beforeSkalaDetails && comparisonData.afterSkalaDetails && (
              <Stack gap='md'>
                <Text fw={600} size='md'>
                  Detailed SkalaCategory Breakdown
                </Text>

                <SimpleGrid cols={2} spacing='md'>
                  {/* Before Assessment Details */}
                  <Paper p='md' withBorder radius='md'>
                    <Text fw={500} mb='md' c='blue'>
                      Before Assessment
                    </Text>
                    <ScrollArea.Autosize mah={300}>
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Category</Table.Th>
                            <Table.Th>Score</Table.Th>
                            <Table.Th>Responses</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {comparisonData.beforeSkalaDetails.map((detail) => (
                            <Table.Tr key={detail.skalaCategory}>
                              <Table.Td>
                                <Text size='sm' fw={500}>
                                  {detail.skalaCategory}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Badge color='blue' variant='light'>
                                  {detail.averageScore.toFixed(1)}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Text size='sm'>{detail.responseCount}</Text>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea.Autosize>
                  </Paper>

                  {/* After Assessment Details */}
                  <Paper p='md' withBorder radius='md'>
                    <Text fw={500} mb='md' c='green'>
                      After Assessment
                    </Text>
                    <ScrollArea.Autosize mah={300}>
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Category</Table.Th>
                            <Table.Th>Score</Table.Th>
                            <Table.Th>Responses</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {comparisonData.afterSkalaDetails.map((detail) => (
                            <Table.Tr key={detail.skalaCategory}>
                              <Table.Td>
                                <Text size='sm' fw={500}>
                                  {detail.skalaCategory}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Badge color='green' variant='light'>
                                  {detail.averageScore.toFixed(1)}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Text size='sm'>{detail.responseCount}</Text>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea.Autosize>
                  </Paper>
                </SimpleGrid>
              </Stack>
            )}

            {/* Module Comparison */}
            {comparisonData.moduleComparison && comparisonData.moduleComparison.length > 0 && (
              <Stack gap='md'>
                <Text fw={600} size='md'>
                  Module Performance Comparison
                </Text>

                <ScrollArea.Autosize mah={250}>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Module</Table.Th>
                        <Table.Th>Before</Table.Th>
                        <Table.Th>After</Table.Th>
                        <Table.Th>Improvement</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {comparisonData.moduleComparison.map((module) => (
                        <Table.Tr key={module.moduleID}>
                          <Table.Td>
                            <Text size='sm' fw={500}>
                              {module.moduleName || module.moduleID}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge color='blue' variant='light'>
                              {module.beforeAverage.toFixed(1)}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Badge color='green' variant='light'>
                              {module.afterAverage.toFixed(1)}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Group gap='xs'>
                              {getImprovementIcon(module.improvement)}
                              <Badge
                                color={getImprovementColor(module.improvement)}
                                variant='light'
                              >
                                {module.improvement > 0 ? '+' : ''}
                                {module.improvement.toFixed(1)}
                              </Badge>
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea.Autosize>
              </Stack>
            )}

            {/* Participant Performance */}
            {comparisonData.participantComparison &&
              comparisonData.participantComparison.length > 0 && (
                <Stack gap='md'>
                  <Text fw={600} size='md'>
                    Individual Participant Progress
                  </Text>

                  <ScrollArea.Autosize mah={250}>
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Participant ID</Table.Th>
                          <Table.Th>Before Score</Table.Th>
                          <Table.Th>After Score</Table.Th>
                          <Table.Th>Improvement</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {comparisonData.participantComparison.map((participant) => (
                          <Table.Tr key={participant.participantID}>
                            <Table.Td>
                              <Group gap='xs'>
                                <IconUsers size={14} />
                                <Text size='sm' fw={500}>
                                  {participant.participantID}
                                </Text>
                              </Group>
                            </Table.Td>
                            <Table.Td>
                              <Badge color='blue' variant='light'>
                                {participant.beforeScore.toFixed(1)}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Badge color='green' variant='light'>
                                {participant.afterScore.toFixed(1)}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Group gap='xs'>
                                {getImprovementIcon(participant.improvement)}
                                <Badge
                                  color={getImprovementColor(participant.improvement)}
                                  variant='light'
                                >
                                  {participant.improvement > 0 ? '+' : ''}
                                  {participant.improvement.toFixed(1)}
                                </Badge>
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </ScrollArea.Autosize>
                </Stack>
              )}

            {/* Action Buttons */}
            <Group justify='flex-end' mt='md'>
              <Button variant='light' onClick={onClose}>
                Close
              </Button>
            </Group>
          </Stack>
        )
      )}
    </Modal>
  );
};

export default ComparisonDetailModal;
